import { formatPackageDuration } from './packageDuration';
import { formatPreferredDate } from './preferredDate';
import { letterPlainText, quoteLetterHtml } from './quoteLetter';
import { formatQuoteMoney, QuotePricingSnapshot } from './quotePricing';
import { mailtoRecipient } from './quoteShare';
import { ShootSummaryInput, shootMapsLink, shootPlace, shootSummaryLines } from './shootSummary';

export function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export interface QuoteDocumentDetails {
  clientName?: string;
  clientEmail?: string;
  personalMessage?: string;
  studioName?: string;
  contactEmail?: string;
  contactPhone?: string;
  validDays?: number;
  clientLink?: string;
  /** The date, time and place asked for. Stated in the document so nothing is left unsaid. */
  shoot?: ShootSummaryInput;
}

function sessionCount(snapshot: QuotePricingSnapshot): number {
  return snapshot.lines.filter((line) => line.kind === 'session').length;
}

function lineWhen(line: QuotePricingSnapshot['lines'][number]): string {
  if (line.kind !== 'session' || (!line.date && !line.startTime)) return '';
  const date = line.date ? (formatPreferredDate(line.date) || line.date) : '';
  return [date, line.startTime].filter(Boolean).join(' at ');
}

function linePlace(line: QuotePricingSnapshot['lines'][number], shoot?: ShootSummaryInput): string {
  if (line.kind !== 'session' || !shoot?.sessions) return '';
  const match = shoot.sessions.find((session) => session.id && line.sessionId && session.id === line.sessionId);
  return shootPlace(match?.location);
}

export function quotePlainText(snapshot: QuotePricingSnapshot, details: QuoteDocumentDetails = {}): string {
  const clientName = details.clientName || 'Valued Client';
  const multiDay = sessionCount(snapshot) > 1;
  const lines = snapshot.lines.map((line) => {
    const amount = line.complimentary ? 'Included' : formatQuoteMoney(line.quoteExtendedMinor, snapshot.currency);
    const coverage = (line.kind === 'package' || line.kind === 'session')
      ? formatPackageDuration(line.durationMinutes)
      : '';
    const when = lineWhen(line);
    const place = linePlace(line, details.shoot);
    const duration = coverage ? `\n  Duration: ${coverage}` : '';
    const note = line.description ? `\n  ${line.description}` : '';
    const features = line.features && line.features.length > 0
      ? `\n${line.features.map((item) => `  - ${item}`).join('\n')}`
      : '';
    return `${line.name}: ${amount}${when ? `\n  ${when}` : ''}${place ? `\n  ${place}` : ''}${duration}${note}${features}`;
  });
  const foreign = snapshot.currency === 'R'
    ? ''
    : `\nShown in ${snapshot.currency} ${formatQuoteMoney(snapshot.quoteTotalMinor, snapshot.currency)}. The rand settlement is ${formatQuoteMoney(snapshot.zarTotalMinor, 'R')}. Card payments are processed in rand.`;
  return [
    details.studioName || 'Carmen Lorraine Photography',
    `Prepared for ${clientName}`,
    details.personalMessage ? letterPlainText(details.personalMessage) : '',
    ...(details.shoot && !multiDay ? shootSummaryLines(details.shoot) : []),
    ...lines,
    `Total: ${formatQuoteMoney(snapshot.quoteTotalMinor, snapshot.currency)}`,
    `Deposit (${snapshot.depositPercent}%): ${formatQuoteMoney(snapshot.depositMinor, 'R')}`,
    `Balance due: ${formatQuoteMoney(snapshot.balanceMinor, 'R')}`,
    foreign,
    'Prices are not subject to VAT.',
  ].filter(Boolean).join('\n');
}

export function quoteMailto(snapshot: QuotePricingSnapshot, recipient: string, details: QuoteDocumentDetails = {}): string {
  const subject = `Quotation from ${details.studioName || 'Carmen Lorraine Photography'}`;
  const link = details.clientLink?.trim();
  const body = link
    ? `${quotePlainText(snapshot, details)}\n\nYour quotation:\n${link}`
    : quotePlainText(snapshot, details);
  return `mailto:${mailtoRecipient(recipient)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function renderQuoteHtml(snapshot: QuotePricingSnapshot, details: QuoteDocumentDetails = {}): string {
  const clientName = escapeHtml(details.clientName || 'Valued Client');
  const message = details.personalMessage ? quoteLetterHtml(details.personalMessage) : '';
  const studioName = escapeHtml(details.studioName || 'Carmen Lorraine Photography');
  const contactEmail = escapeHtml(details.contactEmail || '');
  const contactPhone = escapeHtml(details.contactPhone || '');
  const validDays = Number.isFinite(details.validDays) ? details.validDays : 30;
  const multiDay = sessionCount(snapshot) > 1;
  const shootBlock = details.shoot && !multiDay
    ? `<div class="shoot" style="margin: 18px 0; padding: 12px 0; border-top: 1px solid #ececec; border-bottom: 1px solid #ececec; font-size: 14px; line-height: 1.7; color: #333;">${shootSummaryLines(details.shoot)
      .filter((line) => !line.startsWith('Map: '))
      .map((line) => escapeHtml(line))
      .join('<br>')}${shootMapsLink(details.shoot.location) ? `<br><a href="${escapeHtml(shootMapsLink(details.shoot.location))}" style="color: #111;">Open in Google Maps</a>` : ''}</div>`
    : '';

  const rows = snapshot.lines.map((line) => {
    const label = escapeHtml(line.name);
    const units = line.pricingType === 'per-hour'
      ? ` (${escapeHtml(line.units)} hour${line.units === 1 ? '' : 's'})`
      : line.pricingType === 'per-each' && line.units !== 1
        ? ` × ${escapeHtml(line.units)}`
        : '';
    const amount = line.complimentary
      ? 'Included'
      : escapeHtml(formatQuoteMoney(line.quoteExtendedMinor, snapshot.currency));
    const coverage = (line.kind === 'package' || line.kind === 'session')
      ? formatPackageDuration(line.durationMinutes)
      : '';
    const whenText = lineWhen(line);
    const placeText = linePlace(line, details.shoot);
    const when = whenText
      ? `<span style="display: block; margin-top: 3px; font-size: 13px; line-height: 1.5; color: #5c5c5c;">${escapeHtml(whenText)}</span>`
      : '';
    const place = placeText
      ? `<span style="display: block; margin-top: 3px; font-size: 13px; line-height: 1.5; color: #5c5c5c;">${escapeHtml(placeText)}</span>`
      : '';
    const duration = coverage
      ? `<span style="display: block; margin-top: 3px; font-size: 13px; line-height: 1.5; color: #5c5c5c;">Duration: ${escapeHtml(coverage)}</span>`
      : '';
    const note = line.description
      ? `<span style="display: block; margin-top: 3px; font-size: 13px; line-height: 1.5; color: #5c5c5c;">${escapeHtml(line.description)}</span>`
      : '';
    const features = line.features && line.features.length > 0
      ? `<ul style="margin: 6px 0 0; padding-left: 18px; font-size: 13px; line-height: 1.5; color: #5c5c5c;">${line.features.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`
      : '';
    return `<div style="display: flex; justify-content: space-between; gap: 24px; padding: 10px 0; border-bottom: 1px solid #eee; text-align: left;"><span><strong style="font-weight: 500;">${label}${units}</strong>${when}${place}${duration}${note}${features}</span><span style="white-space: nowrap;">${amount}</span></div>`;
  }).join('');

  const discountRow = snapshot.quoteDiscountMinor > 0
    ? `<div style="display: flex; justify-content: space-between; padding: 10px 0;"><span>Discount (${escapeHtml(snapshot.discountPercent)}%)</span><span>- ${escapeHtml(formatQuoteMoney(snapshot.quoteDiscountMinor, snapshot.currency))}</span></div>`
    : '';

  const foreignNote = snapshot.currency === 'R'
    ? ''
    : `<p style="font-size: 14px; line-height: 1.6; color: #333;">This quotation is priced in ${escapeHtml(snapshot.currency)} ${escapeHtml(formatQuoteMoney(snapshot.quoteTotalMinor, snapshot.currency))}. ${snapshot.settlementPolicy === 'market-v2' ? 'That amount includes the studio currency pricing and rounding, and it settles at the recorded market rate. ' : ''}The rand settlement is ${escapeHtml(formatQuoteMoney(snapshot.zarTotalMinor, 'R'))}. Card payments are processed in rand. Your card issuer may use a different exchange rate and may add its own charges.</p>`;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Quotation</title>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500&family=Playfair+Display:wght@500&display=swap">
  <style>
    body { margin: 0; background: #fff; color: #111; font-family: Inter, Helvetica, sans-serif; }
    .sheet { max-width: 720px; margin: 0 auto; padding: 48px 40px; text-align: center; }
    .mark { width: 64px; height: 64px; }
    .rule { width: 72px; height: 2px; background: #E2B329; margin: 22px auto; }
    .kicker { letter-spacing: 0.22em; text-transform: uppercase; font-size: 11px; margin: 0; }
    h1 { font-family: "Playfair Display", Georgia, serif; font-weight: 500; font-size: 42px; line-height: 1.05; margin: 8px 0 0; }
    .meta, .note, .terms { color: #5c5c5c; font-size: 14px; line-height: 1.6; }
    .row, .totals div { display: flex; justify-content: space-between; gap: 24px; padding: 12px 0; border-bottom: 1px solid #ececec; text-align: left; }
    .total { font-family: "Playfair Display", Georgia, serif; font-size: 32px; border-bottom: 0; padding-top: 22px; }
    @media (max-width: 640px) { .sheet { padding: 28px 20px; } h1 { font-size: 32px; } }
  </style>
</head>
<body>
  <article class="sheet">
    <img class="mark" src="/brand/carmen-lorraine-mark.svg" alt="">
    <p class="kicker">${studioName}</p>
    <h1>${clientName}</h1>
    <p class="meta">Quotation</p>
    <div class="rule"></div>
    ${message ? `<div class="note">${message}</div>` : ''}
    ${shootBlock}
    ${rows}
    ${discountRow}
    <div class="totals">
      <div class="total"><span>Total</span><span>${escapeHtml(formatQuoteMoney(snapshot.quoteTotalMinor, snapshot.currency))}</span></div>
      <div><span>Deposit (${escapeHtml(snapshot.depositPercent)}%)</span><span>${escapeHtml(formatQuoteMoney(snapshot.depositMinor, 'R'))}</span></div>
      <div><span>Balance due</span><span>${escapeHtml(formatQuoteMoney(snapshot.balanceMinor, 'R'))}</span></div>
    </div>
    ${foreignNote}
    <p class="terms">This quotation is valid for ${escapeHtml(validDays)} days. A deposit of ${escapeHtml(snapshot.depositPercent)}% (${escapeHtml(formatQuoteMoney(snapshot.depositMinor, 'R'))}) secures the date. The balance of ${escapeHtml(formatQuoteMoney(snapshot.balanceMinor, 'R'))} is due before the final images are delivered. Travel outside Cape Town is quoted separately. Prices are not subject to VAT.</p>
    <p class="meta">${contactEmail}${contactEmail && contactPhone ? ' · ' : ''}${contactPhone}</p>
  </article>
</body>
</html>`;
}
